import type { MouseEvent } from 'react';
import { Building2 } from 'lucide-react';
import { accentIconProps } from '@/components/accentIcon';
import type { IShellAnchorRect } from '@featit/shared';
import { postShellMessage } from './shellBridge';
import { hasShellCapability } from './shellDetection';
import { useT } from '@/i18n/LocaleProvider';

/** The control's box, which the shell's popover points at. */
export function anchorOf(element: Element): IShellAnchorRect {
  const box = element.getBoundingClientRect();
  return { x: box.x, y: box.y, width: box.width, height: box.height };
}

/**
 * F-0519 (IP-8): the deployment switch, where the account controls are. The iPad app has no
 * navigation bar to hold the deployment button the iPhone has, and its menu bar is not reachable by
 * touch on every iPadOS it supports, so the web offers the switch and the shell answers it with its
 * own sheet: the deployments, and the ways to add another.
 *
 * Renders nothing unless the shell lists `deployments` — never in a browser, the desktop app or the
 * iPhone app.
 *
 * *Mutation:* render it whatever the capability → _renders nothing without the deployments
 * capability_ goes red.
 */
export function ShellDeploymentSwitch({
  variant,
  className,
  iconClassName,
  labelClassName,
  accentPosition,
}: {
  /** `icon` in the sidebar's footer row, `row` in the phone menu's account rows. */
  variant: 'icon' | 'row';
  className: string;
  iconClassName: string;
  labelClassName?: string;
  /**
   * F-0579 ACC-4a, ACC-4b: the switch's place among the icons or rows it stands with, which picks
   * its frame stop in Colour. Unset, it is plain.
   */
  accentPosition?: number;
}) {
  const t = useT();
  const accent = accentPosition === undefined ? {} : accentIconProps(accentPosition);
  if (!hasShellCapability('deployments')) return null;
  const label = t('navigation.sidebar.switchDeployment');
  const choose = (event: MouseEvent<HTMLButtonElement>) =>
    postShellMessage({ type: 'chooseDeployment', anchor: anchorOf(event.currentTarget) });
  return variant === 'icon' ? (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={className}
      onClick={choose}
      data-account-item="deployment"
    >
      <Building2 className={iconClassName} aria-hidden="true" {...accent} />
    </button>
  ) : (
    <button type="button" className={className} onClick={choose} data-account-item="deployment">
      <Building2 className={iconClassName} aria-hidden="true" {...accent} />
      <span className={labelClassName}>{label}</span>
    </button>
  );
}
